import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ActionResult, Platform } from '@multimod/shared';
import type { ChatFeedState, ChatFeedStore } from '@/features/chat-feed/store';
import {
  banApi,
  timeoutApi,
  deleteApi,
  broadcastApi,
  type BanInput,
  type TimeoutInput,
  type DeleteInput,
  type BroadcastInput,
} from './api';

/**
 * Padrão comum de mutation otimista de moderação (#46): marca no store em
 * `onMutate` (UI reflete imediatamente), e reverte tanto em erro de rede
 * (`onError`) quanto quando o worker responde `result.ok === false`
 * (`onSuccess`), sempre com toast em pt-BR. Usado por `useBan` e
 * `useDeleteMessage`. `useTimeout` NÃO usa este helper: seu `mark` depende de
 * `durationSeconds`, então a assinatura diverge.
 */
function useOptimisticAction<I>(
  store: ChatFeedStore,
  mutationFn: (input: I) => Promise<ActionResult>,
  mark: (state: ChatFeedState, input: I) => void,
  unmark: (state: ChatFeedState, input: I) => void,
  errorMessage: (input: I) => string,
) {
  return useMutation({
    mutationFn,
    onMutate: (input: I) => {
      mark(store.getState(), input);
      return { input };
    },
    onSuccess: (result, input) => {
      if (!result.ok) {
        unmark(store.getState(), input);
        toast.error(errorMessage(input));
      }
    },
    onError: (_err, input) => {
      unmark(store.getState(), input);
      toast.error(errorMessage(input));
    },
  });
}

/**
 * Mutation de ban com UI otimista. O caller fornece o store (vem de
 * `useChatFeed(liveChatId, platform).store`) porque o codebase usa store
 * por-liveChatId, não singleton global. UI esconde mensagens do usuário
 * imediatamente; reverte em falha. Toast em pt-BR com `@displayName`.
 */
export function useBan(store: ChatFeedStore) {
  return useOptimisticAction<BanInput>(
    store,
    banApi,
    (state, input) => state.markBannedUser(input.platform, input.target.externalId),
    (state, input) => state.unmarkBannedUser(input.platform, input.target.externalId),
    (input) => `Não foi possível banir @${input.target.displayName}.`,
  );
}

/**
 * Mutation de timeout com UI otimista (Bug D, 2026-05-18).
 *
 * Marca o autor em `timedOutUsers` com TTL natural (`durationSeconds`). O
 * componente da mensagem renderiza badge `TIMED OUT` enquanto a marca está
 * ativa; `ChatFeed` purga as expiradas via tick. Reverte em falha.
 */
export function useTimeout(store: ChatFeedStore) {
  return useMutation({
    mutationFn: timeoutApi,
    onMutate: (input: TimeoutInput) => {
      store.getState().markTimedOutUser(
        input.platform,
        input.target.externalId,
        input.durationSeconds,
      );
      return { input };
    },
    // #51: falha de rede (error) e result.ok===false convergem no mesmo
    // tratamento (unmark + toast). onSettled unifica os dois caminhos e elimina a
    // string de toast duplicada. useTimeout fica fora do useOptimisticAction
    // porque o mark depende de durationSeconds (#46).
    onSettled: (result, error, input) => {
      if (error || !result?.ok) {
        store.getState().unmarkTimedOutUser(input.platform, input.target.externalId);
        toast.error(`Falha ao silenciar @${input.target.displayName}.`);
      }
    },
  });
}

/**
 * Mutation de delete com UI otimista. Marca a mensagem como deleted no store
 * (componente renderiza placeholder "[mensagem apagada]"). Reverte em erro
 * de rede ou `result.ok === false` e mostra toast em pt-BR.
 */
export function useDeleteMessage(store: ChatFeedStore) {
  return useOptimisticAction<DeleteInput>(
    store,
    deleteApi,
    (state, input) => state.markDeleted(input.platform, input.messageExternalId),
    (state, input) => state.unmarkDeleted(input.platform, input.messageExternalId),
    () => 'Não foi possível apagar a mensagem.',
  );
}

function clientBroadcastId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `local-${Date.now()}`;
}

export function useBroadcast(store: ChatFeedStore) {
  return useMutation({
    mutationFn: broadcastApi,
    onMutate: (input: BroadcastInput) => {
      const id = clientBroadcastId();
      const optimisticIds = new Map<Platform, string>();
      const now = Date.now();
      for (const target of input.targets) {
        const externalId = `optimistic:${id}:${target.platform}`;
        optimisticIds.set(target.platform, externalId);
        store.getState().pushOptimisticBroadcast({
          platform: target.platform,
          liveChatId: target.liveChatId,
          externalId,
          text: input.text,
          now,
        });
      }
      return { optimisticIds };
    },
    onSuccess: (response, _input, context) => {
      for (const result of response.results) {
        const optimisticId = context.optimisticIds.get(result.platform);
        if (!optimisticId) continue;
        if (result.ok && result.externalMessageId) {
          store.getState().reconcileOptimisticBroadcast(
            result.platform,
            optimisticId,
            result.externalMessageId,
          );
        } else {
          store.getState().setOptimisticStatus(result.platform, optimisticId, 'failed');
          toast.error('Broadcast falhou em pelo menos uma plataforma.');
        }
      }
    },
    onError: (_err, _input, context) => {
      for (const [platform, optimisticId] of context?.optimisticIds ?? []) {
        store.getState().setOptimisticStatus(platform, optimisticId, 'failed');
      }
      toast.error('Não foi possível enviar o broadcast.');
    },
  });
}
